import { useState, useRef } from 'react'
import {
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { useSnackbar } from 'notistack'
import UseTranslation from '../../hooks/UseTranslation'

/**
 * Subscription reminder bar for employee/manager dashboards.
 * Shows a dismissible warning; on dismiss shows a snackbar with Undo.
 * @param {boolean} [visible=false]
 * - When false, bar is not rendered (e.g. when no subscription warning from API).
 * @param {object} subscriptionReminder - The subscription reminder object.
 */
const SubscriptionReminder = ({ visible = false, subscriptionReminder }) => {
  const { t, language } = UseTranslation()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const snackbarKeyRef = useRef(null)
  const [barState, setBarState] = useState('warning')

  const handleDismiss = () => {
    setBarState('dismissed')
    snackbarKeyRef.current = enqueueSnackbar(
<Box
        onClick={() => closeSnackbar()}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          {t?.['Reminder message dismissed for this session.']}
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation()
              setBarState('warning')
              if (snackbarKeyRef.current != null) closeSnackbar(snackbarKeyRef.current)
            }}
            sx={{ textDecoration: 'underline', fontWeight: 600 }}
          >
            {t?.Undo}
          </Box>
        </Box>
      </Box>,
{
  variant: 'success',
  autoHideDuration: 5000,
},
    )
  }

  if (!visible || barState !== 'warning') return null

  return (
    <Box sx={styles.warningBar}>
      <Box sx={styles.warningBarContent}>
        <Box sx={styles.warningIconWrap}>
          <NotificationsIcon sx={styles.warningBellIcon} />
        </Box>
        <Box sx={styles.warningTextWrap}>
          <Box sx={styles.warningTitleWrap}>
            <Box component="span" sx={styles.warningDot} />
            <Typography sx={styles.warningTitle}>
              {t?.['Reminder Message']}
            </Typography>
          </Box>
          <Typography sx={styles.warningMessage}>
            {language === 'AR' ? subscriptionReminder?.ar_message : subscriptionReminder?.en_message}
          </Typography>
        </Box>
      </Box>
      <IconButton
        aria-label="close"
        onClick={handleDismiss}
        sx={styles.warningCloseBtn}
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

const styles = {
  warningBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
    borderRadius: '12px',
    padding: { xs: '12px 12px 12px 16px', sm: '12px 16px 12px 20px' },
    marginBottom: { xs: '16px', sm: '30px' },
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1.5px solid #FCD34D',
    marginTop: '10px',
  },
  warningBarContent: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
    minWidth: 0,
  },
  warningIconWrap: {
    position: 'relative',
    marginRight: { xs: '12px', sm: '16px' },
    flexShrink: 0,
    backgroundColor: '#F59E0B',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '10px',
  },
  warningBellIcon: {
    width: 24,
    height: 24,
    color: '#ffffff',
  },
  warningDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#F59E0B',
    flexShrink: 0,
    '@keyframes warningDotPulse': {
      '0%, 100%': { transform: 'scale(1)' },
      '50%': { transform: 'scale(1.2)' },
    },
    animation: 'warningDotPulse 2s ease-in-out infinite',
  },
  warningTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  warningTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '10px',
  },
  warningTitle: {
    fontSize: { xs: '14px', sm: '16px' },
    fontFamily: 'Avenir-bold',
    fontWeight: 700,
    color: '#8B4512',
    lineHeight: 1.2,
  },
  warningMessage: {
    fontSize: { xs: '12px', sm: '14px' },
    color: 'rgba(0,0,0,0.75)',
    lineHeight: 1.45,
    marginTop: '4px',
    paddingLeft: { xs: '0px', sm: '20px' },
  },
  warningCloseBtn: {
    color: 'rgba(0,0,0,0.5)',
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: '8px',
    marginLeft: 1,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.1)',
      transform: 'scale(1.1)',
      transition: 'transform 0.6s ease-in-out',
    },
  },
  dismissSnackbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: '#e6f7ed',
    color: '#1e7e34',
    borderRadius: '12px',
    padding: '12px 16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid rgba(30, 126, 52, 0.25)',
    minWidth: 320,
    maxWidth: 560,
  },
  dismissSnackbarIcon: {
    flexShrink: 0,
    width: 24,
    height: 24,
    color: '#1e7e34',
  },
  dismissSnackbarText: {
    flex: 1,
    fontSize: '14px',
    color: '#1e7e34',
  },
  dismissedBarUndo: {
    fontSize: { xs: '13px', sm: '14px' },
    fontWeight: 600,
    color: '#1e7e34',
    textDecoration: 'underline',
    cursor: 'pointer',
    flexShrink: 0,
    '&:hover': {
      opacity: 0.9,
    },
  },
}

export default SubscriptionReminder
